// Turn pictures (22 Oct 2026, Year 2): a `turn_picture` is a simple thing
// (an arrow, a pencil or a key) shown before and after a turn of whole
// quarter turns, clockwise or anticlockwise. With no turn it is the thing on
// its own, facing one way, for a set of options that each show one facing.
// How far it turned, which way, and which way it faces are never written or
// spoken: reading the turn is the question.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export const TURN_THINGS = ['arrow', 'pencil', 'key'] as const;

export type TurnThing = (typeof TURN_THINGS)[number];

// The four ways a thing can face, a quarter turn apart.
export const FACINGS = ['up', 'right', 'down', 'left'] as const;

export type Facing = (typeof FACINGS)[number];

export const TURN_SIZES = ['quarter', 'half', 'three_quarter', 'full'] as const;

export type TurnSize = (typeof TURN_SIZES)[number];

export type TurnDirection = 'clockwise' | 'anticlockwise';

export type TurnPictureSpec = {
  type: 'turn_picture';
  thing: TurnThing;
  // Which way the thing faces before the turn.
  start: Facing;
  // No turn: the thing is drawn once, facing `start`.
  turn: { size: TurnSize; direction: TurnDirection; showPath: boolean } | null;
};

// Quarter turns in each size of turn.
const QUARTERS: Record<TurnSize, number> = {
  quarter: 1,
  half: 2,
  three_quarter: 3,
  full: 4,
};

function isOneOf<T extends string>(
  list: readonly T[],
  value: unknown,
): value is T {
  return (list as readonly unknown[]).includes(value);
}

export function readTurnPicture(
  record: Record<string, unknown>,
): TurnPictureSpec | null {
  const thing = record.thing ?? 'arrow';
  const { start, turn, direction } = record;
  const showPath = record.showPath ?? false;
  if (!isOneOf(TURN_THINGS, thing) || !isOneOf(FACINGS, start)) return null;
  if (typeof showPath !== 'boolean') return null;
  if (turn === undefined) {
    // A thing on its own: a way to turn or a path would have nothing to show.
    if (direction !== undefined || showPath) return null;
    return { type: 'turn_picture', thing, start, turn: null };
  }
  if (!isOneOf(TURN_SIZES, turn)) return null;
  // Always given, because a quarter turn one way ends where three quarters
  // the other way does.
  if (direction !== 'clockwise' && direction !== 'anticlockwise') return null;
  // A full turn ends where it began, so without its path the two halves of
  // the picture are the same and it could not be told from no turn at all.
  if (turn === 'full' && !showPath) return null;
  return {
    type: 'turn_picture',
    thing,
    start,
    turn: { size: turn, direction, showPath },
  };
}

// Degrees clockwise from straight up.
export function facingAngle(facing: Facing): number {
  return FACINGS.indexOf(facing) * 90;
}

// Which way the thing faces after the turn.
export function facingAfter(spec: TurnPictureSpec): Facing {
  if (spec.turn === null) return spec.start;
  const quarters = QUARTERS[spec.turn.size];
  const step = spec.turn.direction === 'clockwise' ? quarters : -quarters;
  return FACINGS[(FACINGS.indexOf(spec.start) + step + 8) % 4] ?? spec.start;
}

const NAMES: Record<TurnThing, string> = {
  arrow: 'an arrow',
  pencil: 'a pencil',
  key: 'a key',
};

// What a screen reader is told in place of the picture: the thing and that
// it turns, never which way it faces, which way it turns or how far.
export function describeTurnPicture(spec: TurnPictureSpec): string {
  const name = NAMES[spec.thing];
  const capital = `${name.charAt(0).toUpperCase()}${name.slice(1)}`;
  if (spec.turn === null) return `${capital}.`;
  const path = spec.turn.showPath
    ? ' A curved path shows which way it turns.'
    : '';
  return `${capital} before and after a turn.${path}`;
}

// What follows is where everything is drawn. The units are close to one
// pixel each when the picture is shown at its natural width.

export type TurnPanel = {
  // Words under the panel; null when the thing is drawn on its own.
  label: 'Before' | 'After' | null;
  cx: number;
  cy: number;
  // Degrees clockwise from up; every thing is drawn facing up, then turned.
  angle: number;
};

export type TurnPath = {
  // An SVG arc around the thing in the Before panel.
  d: string;
  // The three points of its arrowhead.
  head: string;
  clockwise: boolean;
};

export type TurnPicture = {
  panels: TurnPanel[];
  path: TurnPath | null;
  // The side of the square each thing is drawn in.
  thingSize: number;
  panelSize: number;
  width: number;
  height: number;
};

export const TURN_THING_SIZE = 72;
const PANEL = 124;
const PANEL_GAP = 28;
const LABEL_SPACE = 28;
const EDGE = 8;
const PATH_RADIUS = 52;
// A full turn is drawn a little short so its start and end can be seen.
const FULL_SWEEP = 330;

function onCircle(cx: number, cy: number, angle: number, distance: number) {
  const turn = (angle * Math.PI) / 180;
  return {
    x: Number((cx + Math.sin(turn) * distance).toFixed(2)),
    y: Number((cy - Math.cos(turn) * distance).toFixed(2)),
  };
}

function turnPath(
  cx: number,
  cy: number,
  start: number,
  sweep: number,
  clockwise: boolean,
): TurnPath {
  const end = clockwise ? start + sweep : start - sweep;
  const from = onCircle(cx, cy, start, PATH_RADIUS);
  const to = onCircle(cx, cy, end, PATH_RADIUS);
  const large = sweep > 180 ? 1 : 0;
  const sweepFlag = clockwise ? 1 : 0;
  const d = `M ${String(from.x)} ${String(from.y)} A ${String(PATH_RADIUS)} ${String(PATH_RADIUS)} 0 ${String(large)} ${String(sweepFlag)} ${String(to.x)} ${String(to.y)}`;
  // The arrowhead points along the circle, the way the turn goes.
  const radians = (end * Math.PI) / 180;
  const sign = clockwise ? 1 : -1;
  const along = { x: Math.cos(radians) * sign, y: Math.sin(radians) * sign };
  const out = { x: Math.sin(radians), y: -Math.cos(radians) };
  const tip = { x: to.x + along.x * 8, y: to.y + along.y * 8 };
  const left = {
    x: to.x - along.x * 4 + out.x * 7,
    y: to.y - along.y * 4 + out.y * 7,
  };
  const right = {
    x: to.x - along.x * 4 - out.x * 7,
    y: to.y - along.y * 4 - out.y * 7,
  };
  const head = [tip, left, right]
    .map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(' ');
  return { d, head, clockwise };
}

export function turnPicture(spec: TurnPictureSpec): TurnPicture {
  const first = {
    cx: EDGE + PANEL / 2,
    cy: EDGE + PANEL / 2,
    angle: facingAngle(spec.start),
  };
  if (spec.turn === null) {
    return {
      panels: [{ label: null, ...first }],
      path: null,
      thingSize: TURN_THING_SIZE,
      panelSize: PANEL,
      width: PANEL + EDGE * 2,
      height: PANEL + EDGE * 2,
    };
  }
  const { size, direction, showPath } = spec.turn;
  const clockwise = direction === 'clockwise';
  const sweep = size === 'full' ? FULL_SWEEP : QUARTERS[size] * 90;
  return {
    panels: [
      { label: 'Before', ...first },
      {
        label: 'After',
        cx: EDGE + PANEL + PANEL_GAP + PANEL / 2,
        cy: first.cy,
        angle: facingAngle(facingAfter(spec)),
      },
    ],
    path: showPath
      ? turnPath(first.cx, first.cy, first.angle, sweep, clockwise)
      : null,
    thingSize: TURN_THING_SIZE,
    panelSize: PANEL,
    width: PANEL * 2 + PANEL_GAP + EDGE * 2,
    height: PANEL + LABEL_SPACE + EDGE * 2,
  };
}
